/* @flow */

import { _adContainer } from './adcontainer';
import { cleanFrame } from './utils/iframeutils';
import { extend } from './utils/coreutils';

const ONE_STR = '1';
const TWO_STR = '2';
const FRAME_STR = 'F';
const FRAME_PATTERN = new RegExp(`${FRAME_STR}(${ONE_STR}|${TWO_STR})`);

/**
 * @description Implements cached adcontainer functionality
 * @exports cachedAdcontainer
 * @requires module:adcontainer
 */
class _cachedAdContainer extends _adContainer {
  /**
   * Update ad container information for given position
   *
   * @param {object} adInfoObj - Ad info object to be updated
   * @param {boolean} bActive - Should get details about active/inactive?
   * @returns {undefined}
   */
  _updateAdContainerInfo(adInfoObj: adInfo, bActive: boolean = false): adInfo {
    const ret = extend(true, {}, adInfoObj);

    const adFrame = this._getActiveInactiveFrame(adInfoObj.id);
    // if active container is empty use it even if bActive is false
    // this is to take care of showing up ad in first load w/o toggle
    const adDivObj = this._getAdDiv(adFrame.activeContainer);
    const bActive2 = adDivObj && !adDivObj.firstChild ? true : bActive;
    ret.fID = bActive2 ? adFrame.activeFrame : adFrame.inactiveFrame;
    ret.cID = bActive2 ? adFrame.activeContainer : adFrame.inactiveContainer;
    return ret;
  }

  /**
   * Utility functions for adding caching divs
   *
   * @param {string} adPos - ad position
   * @returns {undefined}
   * @private
   */
  _addCachingDivs(adPos: string): void {
    if (this._getAdDiv(adPos + ONE_STR) && this._getAdDiv(adPos + TWO_STR)) {
      return; // already created
    }
    const adDivObj = this._getAdDiv(adPos + this._sWrapper);
    if (adDivObj) {
      adDivObj.innerHTML = `<div id='${adPos}${ONE_STR}' style='display: block;top: 0px;left: 0px;background-color: transparent;'></div>
      <div id='${adPos}${TWO_STR}' style='display: none;top: 0px;left: 0px;background-color: transparent;'></div>`;
    }
  }

  /**
   *
   * Given ID, it will return non-cached ad position name
   *
   * @param {string} [adPos=''] Ad position name
   * @returns {string}  Noncached name
   */
  getNonCachedPosName(adPos: string = ''): string {
    let ret = adPos;
    if (adPos.length > 0 && FRAME_PATTERN.test(adPos)) {
      [ret] = adPos.split(FRAME_PATTERN);
    }
    return ret;
  }

  /**
   * getter for active/inactive adframe details
   *
   * @param {string} adPos - Ad Position name
   * @returns {object} - IDs of active & inactive ad iframe and it's containers
   * @private
   */
  _getActiveInactiveFrame(adPos: string): activeInactiveInfo {
    const adDivObj = this._getAdDiv(`${adPos}${ONE_STR}`);
    const [activeID, inactiveID] =
      adDivObj && adDivObj.style.display !== 'none'
        ? [ONE_STR, TWO_STR]
        : [TWO_STR, ONE_STR];
    return {
      activeContainer: `${adPos}${activeID}`,
      activeFrame: `${adPos}${FRAME_STR}${activeID}`,
      inactiveContainer: `${adPos}${inactiveID}`,
      inactiveFrame: `${adPos}${FRAME_STR}${inactiveID}`
    };
  }

  /**
   * Toggle active & inactive containers
   *
   * @param {string} activeCID - Active ad container ID
   * @param {string} inactiveCID - Inactive ad container ID
   * @returns {undefined}
   * @private
   */
  _toggleAd(activeCID: string, inactiveCID: string) {
    try {
      const active = this._getAdDiv(activeCID);
      const inactive = this._getAdDiv(inactiveCID);
      if (inactive) {
        inactive.style.display = 'block';
      }
      if (active) {
        active.style.display = 'none';
      }
    } catch {
      /* continue regardless of error */
    }
  }

  /**
   * loader iframe AD
   *
   * @param {string} adUrl - static template url for loading ad iframe
   * @param {module:adloader.adInfo} adInfoObj - ad Information
   * @returns {HTMLIFrameElement} - Newly created ad iframe
   */
  loadIframeAd(adUrl: string, adInfoObj: adInfo): ?HTMLIFrameElement {
    this._addCachingDivs(adInfoObj.id);
    return super.loadIframeAd(adUrl, adInfoObj);
  }

  toggleAdByCID(adPos: string = '', activateCID: string = '') {
    if (!activateCID.length || !adPos.length) {
      return;
    }
    // get inactive container
    const adFrame = this._getActiveInactiveFrame(adPos);
    const inactivateCID =
      activateCID === adFrame.activeContainer
        ? adFrame.inactiveContainer
        : adFrame.activeContainer;
    // toggle it
    this._toggleAd(inactivateCID, activateCID);
    // clean up inactive one if needed
    const inactiveDivObj = this._getAdDiv(inactivateCID);
    if (
      inactiveDivObj &&
      inactiveDivObj.firstChild &&
      inactiveDivObj &&
      inactiveDivObj.ownerDocument
    ) {
      cleanFrame(
        inactiveDivObj.ownerDocument,
        inactiveDivObj,
        adFrame.activeFrame
      );
    }
  }

  /**
   * toggle ad during ad rotation
   *
   * @param {string} adPos - Ad position to toggle
   * @returns {undefined}
   */
  toggleAd(adPos: string) {
    const adFrame = this._getActiveInactiveFrame(adPos);
    const inactiveDivObj = this._getAdDiv(adFrame.inactiveContainer);
    const activeDivObj = this._getAdDiv(adFrame.activeContainer);

    // toggle only if inactive container has content
    // this is avoid toggle during first page load
    if (inactiveDivObj && inactiveDivObj.firstChild) {
      this._toggleAd(adFrame.activeContainer, adFrame.inactiveContainer);
      if (activeDivObj && activeDivObj.ownerDocument) {
        cleanFrame(
          activeDivObj.ownerDocument,
          activeDivObj,
          adFrame.activeFrame
        );
      }
    }
  }

  /**
   *
   *Add container divs especially for ad rotation positions
   *
   * @param {string} [adPos=''] - Ad position
   * @returns {undefined}
   * @memberof _cachedAdContainer
   */
  addContainerDivs(adPos: string = '') {
    if (adPos.length) {
      this._addCachingDivs(adPos);
    }
  }
}

// export singleton object as default for this module
const cachedAdcontainer: _cachedAdContainer = new _cachedAdContainer();
export { cachedAdcontainer, _cachedAdContainer };
